import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader } from "lucide-react";
import { toast } from "react-hot-toast";
import Sidebar from "../../../components/Sidebar";
import {
  useCampaign,
  useEligibleUsers,
  useCampaignRedemptions,
  usePendingReviews,
  useApproveTask,
  useRejectTask,
  usePendingRedemptions,
  useApproveRedemption,
  useRejectRedemption,
} from "../hooks/useCampaigns";

const TABS = [
  { key: "tasks", label: "Tasks" },
  { key: "eligible", label: "Eligible Users" },
  { key: "claimed", label: "Claimed By" },
  { key: "reviews", label: "Pending Reviews" },
  { key: "redemptions", label: "Pending Redemptions" },
];

const TaskRow = ({ task }) => (
  <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
    <p className="text-sm font-semibold text-gray-900">{task.description || task.taskType}</p>
    <p className="mt-1 font-mono text-xs text-gray-500">
      {task.taskType} - {JSON.stringify(task.config)}
    </p>
  </div>
);

const UserRow = ({ label, entry }) => (
  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg border border-gray-200">
    <div>
      <p className="text-sm font-semibold text-gray-900">
        {entry.user?.firstName} {entry.user?.lastName}
      </p>
      <p className="text-xs text-gray-500">{entry.user?.email}</p>
    </div>
    <span className="text-xs text-gray-500">{label}</span>
  </div>
);

const PendingReviewRow = ({ review, campaignUuid, onDone }) => {
  const { mutate: approve, isPending: isApproving } = useApproveTask();
  const { mutate: reject, isPending: isRejecting } = useRejectTask();
  const [showReject, setShowReject] = useState(false);
  const [reason, setReason] = useState("");

  const handleApprove = () => {
    approve(
      { uuid: campaignUuid, taskUuid: review.taskUuid, progressUuid: review.progressUuid },
      {
        onSuccess: () => {
          toast.success("Task approved");
          onDone?.();
        },
        onError: (err) => toast.error(err?.response?.data?.message || "Failed to approve"),
      }
    );
  };

  const handleReject = () => {
    if (!reason) return;
    reject(
      {
        uuid: campaignUuid,
        taskUuid: review.taskUuid,
        progressUuid: review.progressUuid,
        payload: { reason },
      },
      {
        onSuccess: () => {
          toast.success("Task rejected");
          setShowReject(false);
          setReason("");
          onDone?.();
        },
        onError: (err) => toast.error(err?.response?.data?.message || "Failed to reject"),
      }
    );
  };

  return (
    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-2">
      <div className="flex justify-between items-start">
        <div>
          <p className="text-sm font-semibold text-gray-900">
            {review.user?.firstName} {review.user?.lastName}
          </p>
          <p className="text-xs text-gray-500">{review.user?.email}</p>
          <p className="mt-1 text-sm text-gray-700">{review.taskDescription}</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleApprove}
            disabled={isApproving}
            className="px-3 py-1.5 text-xs font-bold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
          >
            {isApproving ? "Approving..." : "Approve"}
          </button>
          <button
            onClick={() => setShowReject((v) => !v)}
            className="px-3 py-1.5 text-xs font-bold text-red-600 bg-white rounded-lg border border-red-300 hover:bg-red-50"
          >
            Reject
          </button>
        </div>
      </div>
      <div className="p-2 bg-white rounded border border-gray-200 text-sm text-gray-700">
        {review.evidence?.proofNote}
        {review.evidence?.proofUrl && (
          <>
            {" "}
            -{" "}
            <a
              href={review.evidence.proofUrl}
              target="_blank"
              rel="noreferrer"
              className="text-emerald-700 underline"
            >
              proof link
            </a>
          </>
        )}
      </div>
      {showReject && (
        <div className="flex gap-2">
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason for rejection"
            className="flex-1 px-3 py-2 text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-red-400"
          />
          <button
            onClick={handleReject}
            disabled={!reason || isRejecting}
            className="px-3 py-2 text-xs font-bold text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
          >
            {isRejecting ? "Rejecting..." : "Confirm Reject"}
          </button>
        </div>
      )}
    </div>
  );
};

const PendingRedemptionRow = ({ entry, campaignUuid, onDone }) => {
  const { mutate: approve, isPending: isApproving } = useApproveRedemption();
  const { mutate: reject, isPending: isRejecting } = useRejectRedemption();
  const [showReject, setShowReject] = useState(false);
  const [reason, setReason] = useState("");

  const handleApprove = () => {
    approve(
      { uuid: campaignUuid, progressUuid: entry.uuid },
      {
        onSuccess: () => {
          toast.success("Redemption approved - reward credited");
          onDone?.();
        },
        onError: (err) => toast.error(err?.response?.data?.message || "Failed to approve"),
      }
    );
  };

  const handleReject = () => {
    if (!reason) return;
    reject(
      { uuid: campaignUuid, progressUuid: entry.uuid, payload: { reason } },
      {
        onSuccess: () => {
          toast.success("Redemption rejected");
          setShowReject(false);
          setReason("");
          onDone?.();
        },
        onError: (err) => toast.error(err?.response?.data?.message || "Failed to reject"),
      }
    );
  };

  return (
    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-2">
      <div className="flex justify-between items-start">
        <div>
          <p className="text-sm font-semibold text-gray-900">
            {entry.user?.firstName} {entry.user?.lastName}
          </p>
          <p className="text-xs text-gray-500">{entry.user?.email}</p>
          <p className="mt-1 text-xs text-gray-400">
            Requested{" "}
            {entry.redemptionRequestedAt ? new Date(entry.redemptionRequestedAt).toLocaleString() : ""}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleApprove}
            disabled={isApproving}
            className="px-3 py-1.5 text-xs font-bold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
          >
            {isApproving ? "Approving..." : "Approve & Credit"}
          </button>
          <button
            onClick={() => setShowReject((v) => !v)}
            className="px-3 py-1.5 text-xs font-bold text-red-600 bg-white rounded-lg border border-red-300 hover:bg-red-50"
          >
            Reject
          </button>
        </div>
      </div>
      {showReject && (
        <div className="flex gap-2">
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason for rejection"
            className="flex-1 px-3 py-2 text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-red-400"
          />
          <button
            onClick={handleReject}
            disabled={!reason || isRejecting}
            className="px-3 py-2 text-xs font-bold text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
          >
            {isRejecting ? "Rejecting..." : "Confirm Reject"}
          </button>
        </div>
      )}
    </div>
  );
};

const CampaignDetailTabs = () => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("tasks");

  const { data: campaignRes, isLoading: campaignLoading } = useCampaign(uuid);
  const { data: eligibleRes, isLoading: eligibleLoading } = useEligibleUsers(uuid);
  const { data: redemptionsRes, isLoading: redemptionsLoading } = useCampaignRedemptions(uuid);
  const {
    data: pendingReviewsRes,
    isLoading: pendingReviewsLoading,
    refetch: refetchPendingReviews,
  } = usePendingReviews(uuid);
  const {
    data: pendingRedemptionsRes,
    isLoading: pendingRedemptionsLoading,
    refetch: refetchPendingRedemptions,
  } = usePendingRedemptions(uuid);

  const campaign = campaignRes?.data;
  const eligibleUsers = eligibleRes?.data || [];
  const redemptions = redemptionsRes?.data || [];
  const pendingReviews = pendingReviewsRes?.data || [];
  const pendingRedemptions = pendingRedemptionsRes?.data || [];

  return (
    <div className="flex overflow-hidden flex-col h-screen md:flex-row">
      <Sidebar />

      <div className="overflow-y-auto flex-1 p-4 bg-white md:py-7">
        <button
          onClick={() => navigate("/coupon-campaigns")}
          className="flex gap-1 items-center mb-4 text-sm font-medium text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft size={16} /> Back to Campaigns
        </button>

        {campaignLoading ? (
          <div className="flex gap-2 items-center text-gray-600">
            Loading campaign <Loader className="w-4 h-4 animate-spin" />
          </div>
        ) : (
          <>
            <h1 className="mb-1 text-xl font-bold md:text-2xl">{campaign?.name}</h1>
            <p className="mb-6 text-sm text-gray-500">
              Reward: {campaign?.rewardCurrency} {Number(campaign?.rewardAmount || 0).toLocaleString()} · Code:{" "}
              {campaign?.code || "—"} · Status: {campaign?.status}
            </p>

            <div className="flex gap-2 mb-6 border-b border-gray-200">
              {TABS.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
                    activeTab === tab.key
                      ? "border-emerald-600 text-emerald-700"
                      : "border-transparent text-gray-500 hover:text-gray-800"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {activeTab === "tasks" && (
              <div className="space-y-2">
                {(campaign?.tasks || []).map((task, i) => (
                  <TaskRow key={i} task={task} />
                ))}
                {(campaign?.tasks || []).length === 0 && (
                  <p className="text-sm text-gray-500">No tasks configured.</p>
                )}
              </div>
            )}

            {activeTab === "eligible" && (
              <div className="space-y-2">
                {eligibleLoading ? (
                  <p className="text-sm text-gray-500">Loading...</p>
                ) : eligibleUsers.length > 0 ? (
                  eligibleUsers.map((entry) => (
                    <UserRow key={entry.uuid} label="All tasks completed" entry={entry} />
                  ))
                ) : (
                  <p className="text-sm text-gray-500">
                    No users have completed every task for this campaign yet.
                  </p>
                )}
              </div>
            )}

            {activeTab === "claimed" && (
              <div className="space-y-2">
                {redemptionsLoading ? (
                  <p className="text-sm text-gray-500">Loading...</p>
                ) : redemptions.length > 0 ? (
                  redemptions.map((entry) => (
                    <UserRow
                      key={entry.uuid}
                      label={entry.redeemedAt ? new Date(entry.redeemedAt).toLocaleString() : ""}
                      entry={entry}
                    />
                  ))
                ) : (
                  <p className="text-sm text-gray-500">No one has claimed this campaign yet.</p>
                )}
              </div>
            )}

            {activeTab === "reviews" && (
              <div className="space-y-2">
                {pendingReviewsLoading ? (
                  <p className="text-sm text-gray-500">Loading...</p>
                ) : pendingReviews.length > 0 ? (
                  pendingReviews.map((review) => (
                    <PendingReviewRow
                      key={`${review.progressUuid}-${review.taskUuid}`}
                      review={review}
                      campaignUuid={uuid}
                      onDone={refetchPendingReviews}
                    />
                  ))
                ) : (
                  <p className="text-sm text-gray-500">
                    No custom-request submissions awaiting approval.
                  </p>
                )}
              </div>
            )}

            {activeTab === "redemptions" && (
              <div className="space-y-2">
                {!campaign?.requiresApprovalToRedeem && (
                  <p className="mb-2 text-sm text-gray-500">
                    This campaign doesn't require manual review before redemption - enable
                    "Require manual review before redemption" when editing it to use this tab.
                  </p>
                )}
                {pendingRedemptionsLoading ? (
                  <p className="text-sm text-gray-500">Loading...</p>
                ) : pendingRedemptions.length > 0 ? (
                  pendingRedemptions.map((entry) => (
                    <PendingRedemptionRow
                      key={entry.uuid}
                      entry={entry}
                      campaignUuid={uuid}
                      onDone={refetchPendingRedemptions}
                    />
                  ))
                ) : (
                  <p className="text-sm text-gray-500">
                    No redemption requests awaiting approval.
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CampaignDetailTabs;
